import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, DollarSign, Calendar, Users, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    { icon: Gift, title: 'Gift Lists', description: 'Create lists for everyone' },
    { icon: DollarSign, title: 'Budgets', description: 'Track every dollar' },
    { icon: Calendar, title: 'Events', description: 'Organize by holiday' },
  ];

  const premiumFeatures = [
    'Recipient profiles & history',
    'Reminders & notifications',
    'Share lists & export',
    'Spending reports',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-green-50">
      {/* Snowflakes */}
      <div className="snowflake-container">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="snowflake"
            style={{
              left: `${Math.random() * 100}%`,
              animationDuration: `${Math.random() * 3 + 2}s`,
              animationDelay: `${Math.random() * 2}s`,
              fontSize: `${Math.random() * 10 + 10}px`,
            }}
          >
            ❄
          </div>
        ))}
      </div>

      {/* Header */}
      <header className="relative z-10 p-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Gift className="w-8 h-8 text-red-600" />
          <span className="text-2xl font-bold gradient-text">Gifty</span>
        </div>
        <div className="flex gap-4">
          <Button variant="ghost" onClick={() => navigate('/auth')} data-testid="signin-button">
            Sign In
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 py-20 text-center">
        <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
          <span className="gradient-text">Never Forget a Gift,</span>
          <br />
          <span className="gradient-text">Never Overspend Again</span>
        </h1>
        <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
          Stay organized, within budget, and stress-free this holiday season—and all year round.
        </p>
        <Button
          size="lg"
          className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg"
          onClick={() => navigate('/auth')}
          data-testid="get-started-button"
        >
          🎄 Get Started Free
        </Button>
      </section>

      {/* Features Section */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-12">Everything You Need</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, idx) => (
            <Card key={idx} className="glass-card border-red-100">
              <CardContent className="p-6 text-center">
                <feature.icon className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Premium Teaser */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-8">Unlock More with Premium & Elite</h2>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="glass-card border-red-300">
            <CardContent className="p-6">
              <h3 className="text-2xl font-bold text-center mb-4">
                <span className="text-red-600">Premium</span> - $19.99/year
              </h3>
              <ul className="space-y-2 mb-4">
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Recipient profiles & history</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Share lists</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Export to CSV/PDF</span>
                </li>
              </ul>
            </CardContent>
          </Card>
          
          <Card className="glass-card border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50">
            <CardContent className="p-6">
              <h3 className="text-2xl font-bold text-center mb-4">
                <span className="text-amber-600">⭐ Elite</span> - $39.99/year
              </h3>
              <ul className="space-y-2 mb-4">
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="font-semibold">Everything in Premium +</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Received gifts tracking</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Email reminders</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Spending reports</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Priority support</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => navigate('/subscription')}
            className="border-red-600 text-red-600 hover:bg-red-50"
          >
            View All Plans & Features
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 max-w-6xl mx-auto px-4 py-8 mt-16 border-t border-red-100">
        <div className="text-center text-gray-600">
          <p>© 2025 Gifty. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
